import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import {
  MeasLaboratoryPrescription,
  MeasLaboratoryPrescriptionSchema,
} from "@/modules/patients/detail/meas-laboratory-prescription/ch/schema/meas-laboratory-prescription-schema"
import { chMeasLaboratoryPrescriptionFragment$key } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/__generated__/chMeasLaboratoryPrescriptionFragment.graphql"
import { chMeasLaboratoryPrescriptionFragment } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/chMeasLaboratoryPrescriptionFragment"
import { useChCreateMeasLaboratoryPrescriptionMutation } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/useChCreateMeasLaboratoryPrescriptionMutation"
import { useChDeleteMeasLaboratoryPrescriptionMutation } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/useChDeleteMeasLaboratoryPrescriptionMutation"
import { useChGenerateMeasLaboratoryPrescriptionMutation } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/useChGenerateMeasLaboratoryPrescriptionMutation"
import { useChUpdateMeasLaboratoryPrescriptionMutation } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/useChUpdateMeasLaboratoryPrescriptionMutation"
import {
  BLOOD_CHEMISTRIES,
  DOCUMENT_LANGUAGES,
  HEMATOLOGIES,
} from "@/modules/patients/detail/meas-laboratory-prescription/constants"
import { getDocumentLanguageLabel } from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { getUploadSessionCode } from "@/modules/patients/libs/get-upload-session-code"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { CoagulationValues, RapidTestValues, UrineDiagnosticValues } from "@/types/__generated__/union-types"

interface ChMeasLaboratoryPrescriptionDrawerProps {
  progressNoteId: string
  id?: string
  measLaboratoryPrescriptionRef?: chMeasLaboratoryPrescriptionFragment$key
  fileUploadRequest?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["fileUploadRequest"]
  medgateConnectOrder?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["medgateConnectOrder"]
}

const ChMeasLaboratoryPrescriptionDrawer = withErrorBoundary(
  ({
    measLaboratoryPrescriptionRef,
    progressNoteId,
    id,
    fileUploadRequest,
    medgateConnectOrder,
  }: ChMeasLaboratoryPrescriptionDrawerProps) => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const [createMeasLaboratoryPrescription, isCreating] = useChCreateMeasLaboratoryPrescriptionMutation()
    const [updateMeasLaboratoryPrescription, isUpdating] = useChUpdateMeasLaboratoryPrescriptionMutation()
    const [generateMeasLaboratoryPrescription, isGenerating] = useChGenerateMeasLaboratoryPrescriptionMutation()
    const [deleteMeasLaboratoryPrescription] = useChDeleteMeasLaboratoryPrescriptionMutation()
    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)

    const patientId = usePatientParam()
    const isEditMode = !!id
    const isAddMode = !isEditMode && patientId && progressNoteId
    const persistedData = useFragment(chMeasLaboratoryPrescriptionFragment, measLaboratoryPrescriptionRef ?? null)
    const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy

    const parsedData =
      isEditMode && persistedData
        ? MeasLaboratoryPrescriptionSchema.parse({
            ...persistedData,
          })
        : null

    const form = useForm<MeasLaboratoryPrescription>({
      mode: "all",
      resolver: zodResolver(MeasLaboratoryPrescriptionSchema),
      defaultValues:
        isEditMode && parsedData
          ? {
              ...parsedData,
            }
          : {
              language: "GERMAN",
              bloodChemistries: [],
              hematologies: [],
              coagulations: [],
              urineDiagnostics: [],
              rapidTests: [],
            },
    })

    const {
      control,
      formState: { isDirty },
    } = form

    useDirtyDrawerMaker(isDirty)

    const onSubmit: SubmitHandler<MeasLaboratoryPrescription> = (data) => {
      if (!progressNoteId) return

      const formattedData = {
        ...data,
      }

      if (isAddMode) {
        createMeasLaboratoryPrescription({
          variables: {
            input: {
              patientId,
              progressNoteId,
              language: formattedData.language,
              bloodChemistries: formattedData.bloodChemistries,
              hematologies: formattedData.hematologies,
              coagulations: formattedData.coagulations,
              urineDiagnostics: formattedData.urineDiagnostics,
              rapidTests: formattedData.rapidTests,
            },
          },
          onCompleted(response) {
            const id = response.ch_createMeasLaboratoryPrescription.ch_MeasLaboratoryPrescription?.id
            if (id) {
              handleGenerateMeasLaboratoryPrescriptionDocument(id)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const prescription = data?.ch_createMeasLaboratoryPrescription?.ch_MeasLaboratoryPrescription

              if (prescription?.id) {
                const newRecordProxy = store.get(prescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_measLaboratoryPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode && id) {
        updateMeasLaboratoryPrescription({
          variables: {
            input: {
              id,
              language: formattedData.language,
              bloodChemistries: formattedData.bloodChemistries,
              hematologies: formattedData.hematologies,
              coagulations: formattedData.coagulations,
              urineDiagnostics: formattedData.urineDiagnostics,
              rapidTests: formattedData.rapidTests,
            },
          },
          onCompleted(response) {
            const id = response.ch_updateMeasLaboratoryPrescription.ch_MeasLaboratoryPrescription?.id
            if (id) {
              handleGenerateMeasLaboratoryPrescriptionDocument(id)
            }
          },
        })
      }
    }

    const onDelete = () => {
      if (progressNoteId && id) {
        deleteMeasLaboratoryPrescription({
          variables: {
            input: {
              id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.measLaboratoryPrescription.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.measLaboratoryPrescription.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
      })
    }

    const handleCloseDrawer = () => {
      closeDrawer()
      FaroInsightsWrapper.pushEvent("documents_meas_lab_close")
    }

    const handleGenerateMeasLaboratoryPrescriptionDocument = (id: string) => {
      if (id) {
        generateMeasLaboratoryPrescription({
          id,
          uploadSessionCode: getUploadSessionCode(fileUploadRequest, medgateConnectOrder),
          onCompleted() {
            handleCloseDrawer()
          },
        })
      }
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="meas-laboratory-prescription-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("patient.measLaboratoryPrescription.measLaboratoryPrescription")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="meas-laboratory-prescription-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({ field: { name, value, onChange }, formState: { errors }, fieldState: { invalid } }) => (
                    <div>
                      <Select value={value} onValueChange={onChange} disabled>
                        <SelectTrigger hasError={invalid} className="w-56.5" data-testid="document-language-select">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.measLaboratoryPrescription.documentLanguage")}
                              value={t(getDocumentLanguageLabel(value))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>

              <div className="flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">
                  {t("patient.measLaboratoryPrescription.for")}
                </label>
                <div className="flex flex-col gap-2">
                  <label className="text-on-surface-subdued text-sm">
                    {t(`patient.measLaboratoryPrescription.bloodChemistry`)}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {BLOOD_CHEMISTRIES.map((bloodChemistry) => (
                      <FormField
                        key={bloodChemistry}
                        control={control}
                        name="bloodChemistries"
                        render={({ field: { value = [], onChange } }) => (
                          <DetailCheckbox
                            key={bloodChemistry}
                            checked={value.includes(bloodChemistry)}
                            data-testid={`${bloodChemistry.toLowerCase()}-blood-chemistry-checkbox`}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                onChange([...value, bloodChemistry].sort((a, b) => a.localeCompare(b)))
                              } else {
                                onChange(value.filter((value) => value !== bloodChemistry))
                              }
                            }}>
                            {t(`patient.measLaboratoryPrescription.bloodChemistries.${bloodChemistry}`)}
                          </DetailCheckbox>
                        )}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-on-surface-subdued text-sm">
                    {t(`patient.measLaboratoryPrescription.hematology`)}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {HEMATOLOGIES.map((hematology) => (
                      <FormField
                        key={hematology}
                        control={control}
                        name="hematologies"
                        render={({ field: { value = [], onChange } }) => (
                          <DetailCheckbox
                            key={hematology}
                            checked={value.includes(hematology)}
                            data-testid={`${hematology.toLowerCase()}-hematology-checkbox`}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                onChange([...value, hematology].sort((a, b) => a.localeCompare(b)))
                              } else {
                                onChange(value.filter((value) => value !== hematology))
                              }
                            }}>
                            {t(`patient.measLaboratoryPrescription.hematologies.${hematology}`)}
                          </DetailCheckbox>
                        )}
                      />
                    ))}
                  </div>
                  <label className="text-on-surface-subdued mx-3 -mb-2 text-xs leading-4 tracking-[0.25px]">
                    {t(`patient.measLaboratoryPrescription.hematologyII`)}
                  </label>
                  <label className="text-on-surface-subdued mx-3 text-xs leading-4 tracking-[0.25px]">
                    {t(`patient.measLaboratoryPrescription.hematologyV`)}
                  </label>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-on-surface-subdued text-sm">
                    {t(`patient.measLaboratoryPrescription.coagulation`)}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CoagulationValues.map((coagulation) => (
                      <FormField
                        key={coagulation}
                        control={control}
                        name="coagulations"
                        render={({ field: { value = [], onChange } }) => (
                          <DetailCheckbox
                            key={coagulation}
                            checked={value.includes(coagulation)}
                            data-testid={`${coagulation.toLowerCase()}-coagulation-checkbox`}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                onChange([...value, coagulation].sort((a, b) => a.localeCompare(b)))
                              } else {
                                onChange(value.filter((value) => value !== coagulation))
                              }
                            }}>
                            {t(`patient.measLaboratoryPrescription.coagulations.${coagulation}`)}
                          </DetailCheckbox>
                        )}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-on-surface-subdued text-sm">
                    {t(`patient.measLaboratoryPrescription.urineDiagnostic`)}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {UrineDiagnosticValues.map((urineDiagnostic) => (
                      <FormField
                        key={urineDiagnostic}
                        control={control}
                        name="urineDiagnostics"
                        render={({ field: { value = [], onChange } }) => (
                          <DetailCheckbox
                            key={urineDiagnostic}
                            checked={value.includes(urineDiagnostic)}
                            data-testid={`${urineDiagnostic.toLowerCase()}-urine-diagnostic-checkbox`}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                onChange([...value, urineDiagnostic].sort((a, b) => a.localeCompare(b)))
                              } else {
                                onChange(value.filter((value) => value !== urineDiagnostic))
                              }
                            }}>
                            {t(`patient.measLaboratoryPrescription.urineDiagnostics.${urineDiagnostic}`)}
                          </DetailCheckbox>
                        )}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-on-surface-subdued text-sm">
                    {t(`patient.measLaboratoryPrescription.rapidTest`)}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {RapidTestValues.map((rapidTest) => (
                      <FormField
                        key={rapidTest}
                        control={control}
                        name="rapidTests"
                        render={({ field: { value = [], onChange } }) => (
                          <DetailCheckbox
                            key={rapidTest}
                            checked={value.includes(rapidTest)}
                            data-testid={`${rapidTest.toLowerCase()}-rapid-test-checkbox`}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                onChange([...value, rapidTest].sort((a, b) => a.localeCompare(b)))
                              } else {
                                onChange(value.filter((value) => value !== rapidTest))
                              }
                            }}>
                            {t(`patient.measLaboratoryPrescription.rapidTests.${rapidTest}`)}
                          </DetailCheckbox>
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="meas-laboratory-prescription-footer">
              {isEditMode && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                  <button
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_meas_lab_delete")
                      handleDelete()
                    }}
                    data-testid="meas-laboratory-prescription-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  data-testid="meas-laboratory-prescription-cancel-button"
                  variant="secondary"
                  onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  disabled={isEditMode && !isDirty}
                  data-testid="meas-laboratory-prescription-save-button"
                  isLoading={isCreating || isUpdating || isGenerating}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_meas_lab_save")
                    form.handleSubmit(onSubmit)()
                  }}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChMeasLaboratoryPrescriptionDrawer",
)

export { ChMeasLaboratoryPrescriptionDrawer }
